/* eslint-disable react-hooks/exhaustive-deps */
import { Box } from '@mui/material';
import { useCallback, useState } from 'react';

import {
  AssociationWOData,
  CheckWorkOrderRegistryParams,
} from '../../../api/registryAssociation/types';
import { AssociateWorkOrderDialog } from '../../../components/AssociateWorkOrderDialog';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import { PeptDialog } from '../../../components/PeptDialog';
import { FlexSpaceBetweenBox } from '../../../components/UI/Box';
import {
  ColoredCircularProgress,
  WhiteCircularProgress,
} from '../../../components/UI/Typography';
import { Constants } from '../../../constants/entrance';
import { isPlaceholderSelectOption } from '../../../helpers';
import { useAssociateWorkOrder } from '../../../hooks/useAssociateWorkOrder';
import { useChangeStatus } from '../../../hooks/useChangeStatus';
import useErrorMessage from '../../../hooks/useErrorMessage';
import { AssociateButton, EditButton, EditSubmitBox } from '../styles';
import EntranceConfirmationDialog from './EntranceConfirmationDialog';

interface SubmitButtonsProps {
  isHiveOwner: boolean;
  osId: number;
  referenceNumber: number;
  registerNumber: string;
  book: string;
  registryOffice: string;
  submitLoading: boolean;
  isOSAccepted: boolean;
  isOSRejected: boolean;
  removeRequestLoading: boolean;
  handleRemoveRequest: () => Promise<void>;
  shouldAssociate: boolean | null;
  handleAssociate: (value: boolean | null) => void;
  setAssociateIds: (values: number[]) => void;
  hasAssociation: boolean;
}

export default function SubmitButtons({
  isHiveOwner,
  osId,
  referenceNumber,
  registerNumber,
  book,
  registryOffice,
  submitLoading,
  isOSAccepted,
  isOSRejected,
  removeRequestLoading,
  handleRemoveRequest,
  shouldAssociate,
  handleAssociate,
  setAssociateIds,
  hasAssociation,
}: SubmitButtonsProps): JSX.Element {
  const [openAssociateDialog, setOpenAssociateDialog] = useState(false);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [workOrders, setWorkOrders] = useState<AssociationWOData[]>([]);
  const [isApprove, setIsApprove] = useState(false);

  const { handleStatus, loadingApprove } = useChangeStatus();
  const { getMatchRegistryWorkOders, loading: loadingAssociateData } =
    useAssociateWorkOrder();
  const { logDevError } = useErrorMessage();

  const onCloseAssociateDialog = (): void => setOpenAssociateDialog(false);
  const onCloseApproveDialog = (): void => setOpenApproveDialog(false);

  const checkAndHandleAssociation = useCallback(
    async (onSuccess: () => void | Promise<void>) => {
      try {
        const params: CheckWorkOrderRegistryParams = {
          book,
          registration_number: registerNumber,
          registry_office: registryOffice,
          exclude_work_order_id: osId,
        };

        const result = await getMatchRegistryWorkOders(params);

        if (!result || result.length === 0) {
          await onSuccess();
          return;
        }

        const associateIds = result.map((os) => os.id);
        setAssociateIds(associateIds);
        setWorkOrders(result);
        setOpenAssociateDialog(true);
      } catch (error) {
        logDevError(
          `Error on loading match work orders to associate: ${error}`
        );
        await onSuccess();
      }
    },
    [book, registerNumber, registryOffice, osId, getMatchRegistryWorkOders]
  );

  const submitForm = (): Promise<void> => {
    return new Promise((resolve) => {
      const form = document.getElementById('entrance');
      if (form instanceof HTMLFormElement) {
        form.requestSubmit();
        setTimeout(resolve, 100);
      } else {
        resolve();
      }
    });
  };

  const handleConfirmEditingButton = useCallback(async () => {
    handleAssociate(null);
    setIsApprove(false);

    const hasInvalidFields = [registerNumber, book, registryOffice].some(
      isPlaceholderSelectOption
    );

    if (hasInvalidFields || hasAssociation) {
      submitForm();
      return;
    }

    await checkAndHandleAssociation(submitForm);
  }, [
    registerNumber,
    book,
    registryOffice,
    hasAssociation,
    checkAndHandleAssociation,
  ]);

  const handleApproveButton = useCallback(async () => {
    handleAssociate(null);
    setIsApprove(true);

    if (hasAssociation) {
      setOpenApproveDialog(true);
      return;
    }

    await checkAndHandleAssociation(() => setOpenApproveDialog(true));
  }, [hasAssociation, checkAndHandleAssociation]);

  const renderEditButton = (
    <EditButton
      onClick={handleConfirmEditingButton}
      disabled={submitLoading || loadingApprove || loadingAssociateData}
    >
      {!isApprove && (submitLoading || loadingAssociateData) ? (
        <ColoredCircularProgress size={22} />
      ) : (
        Constants.confirmEdit
      )}
    </EditButton>
  );

  const renderApproveDialog = (
    <AssociateButton
      onClick={handleApproveButton}
      disabled={submitLoading || loadingApprove || loadingAssociateData}
    >
      {isApprove && (submitLoading || loadingAssociateData) ? (
        <WhiteCircularProgress size={22} />
      ) : (
        Constants.approve
      )}
    </AssociateButton>
  );

  const renderRemoveDialog = (
    <ConfirmationDialog
      loading={removeRequestLoading}
      title={Constants.remove}
      text={Constants.removeRequest}
      modalCallback={handleRemoveRequest}
    />
  );

  let content;

  if (!isHiveOwner) {
    content = (
      <>
        {renderEditButton}
        {renderApproveDialog}
      </>
    );
  } else if (isOSRejected) {
    content = renderRemoveDialog;
  } else if (!isOSAccepted) {
    content = renderEditButton;
  }
  return (
    <>
      <EditSubmitBox>
        {!isHiveOwner ? (
          <PeptDialog osId={osId} referenceNumber={referenceNumber} />
        ) : (
          <Box />
        )}
        <FlexSpaceBetweenBox gap="20px">{content}</FlexSpaceBetweenBox>
      </EditSubmitBox>
      <AssociateWorkOrderDialog
        workOrders={workOrders}
        registerNumber={registerNumber}
        open={openAssociateDialog}
        handleClose={onCloseAssociateDialog}
        isEntrance
        shouldAssociate={shouldAssociate}
        handleAssociate={handleAssociate}
        callback={
          isApprove ? () => handleStatus(osId, referenceNumber) : undefined
        }
      />
      <EntranceConfirmationDialog
        referenceNumber={referenceNumber}
        open={openApproveDialog}
        handleClose={onCloseApproveDialog}
        modalCallback={() => handleStatus(osId, referenceNumber)}
      />
    </>
  );
}
